import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadCloud, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const ALLOWED_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'video/mp4': ['.mp4'],
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
};

//const MAX_SIZE_BYTES = 50 * 1024 * 1024;
const MAX_SIZE_BYTES = 3 * 1024 * 1024 * 1024; // 3 GB

interface FileUploadZoneProps {
  onFilesAdded: (files: File[]) => void;
  currentCount: number;
  maxFiles?: number;
  disabled?: boolean;
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFilesAdded,
  currentCount,
  maxFiles = 50,
  disabled = false,
}) => {
  const remaining = maxFiles - currentCount;

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      rejectedFiles.forEach((rejected) => {
        rejected.errors.forEach((error: any) => {
          if (error.code === 'file-too-large') {
            toast.error(
              `${rejected.file.name}: File exceeds 3 GB limit`
            );
          } else if (error.code === 'file-invalid-type') {
            toast.error(
              `${rejected.file.name}: File type not allowed`
            );
          } else if (error.code === 'too-many-files') {
            toast.error(`Maximum ${maxFiles} files allowed at once`);
          }
        });
      });

      if (acceptedFiles.length > remaining) {
        toast.error(
          `You can only add ${remaining} more file${
            remaining !== 1 ? 's' : ''
          }`
        );
        onFilesAdded(acceptedFiles.slice(0, remaining));
      } else {
        onFilesAdded(acceptedFiles);
      }
    },
    [remaining, maxFiles, onFilesAdded]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept: ALLOWED_TYPES,
      maxSize: MAX_SIZE_BYTES,
      maxFiles: remaining,
      disabled: disabled || remaining === 0,
    });

  const borderColor = isDragReject
    ? 'var(--color-error)'
    : isDragActive
    ? 'var(--color-primary)'
    : 'var(--color-gray-300)';

  const bgColor = isDragReject
    ? 'var(--color-error-light)'
    : isDragActive
    ? 'var(--color-primary-50)'
    : 'var(--color-gray-50)';

  return (
    <div
      {...getRootProps()}
      style={{
        border: `2px dashed ${borderColor}`,
        borderRadius: 'var(--radius-lg)',
        background: bgColor,
        padding: 'clamp(var(--spacing-4), 5vw, var(--spacing-10))',
        textAlign: 'center',
        cursor:
          disabled || remaining === 0 ? 'not-allowed' : 'pointer',
        transition: 'all var(--transition-base)',
        opacity: disabled || remaining === 0 ? 0.5 : 1,
      }}
    >
      <input {...getInputProps()} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--spacing-3)',
        }}
      >
        {isDragReject ? (
          <AlertCircle
            size={36}
            style={{ color: 'var(--color-error)' }}
          />
        ) : (
          <UploadCloud
            size={36}
            style={{
              color: isDragActive
                ? 'var(--color-primary)'
                : 'var(--color-gray-400)',
            }}
          />
        )}
        <div>
          <p
            style={{
              fontWeight: 600,
              color: 'var(--color-gray-700)',
              marginBottom: 4,
            }}
          >
            {isDragActive
              ? 'Drop files here'
              : remaining === 0
              ? 'Maximum files reached'
              : 'Drag & drop files here'}
          </p>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-gray-500)',
            }}
          >
            or{' '}
            <span
              style={{
                color: 'var(--color-primary)',
                fontWeight: 500,
              }}
            >
              click to browse
            </span>
          </p>
        </div>
        <p
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-gray-400)',
            lineHeight: 1.5,
            overflowWrap: 'anywhere',
          }}
        >
          PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, MP4, MP3, WAV — Max
          50MB per file — Up to {maxFiles} files
        </p>
        {currentCount > 0 && (
          <p
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-gray-500)',
            }}
          >
            {currentCount} file{currentCount !== 1 ? 's' : ''} added ·{' '}
            {remaining} remaining
          </p>
        )}
      </div>
    </div>
  );
};

export default FileUploadZone;
